import api from 'services/api';
import { REJECT, array, nullable, numeric, object, oneOf, str, type Received } from 'validation';
import { readResponse } from 'api/shared/readResponse';

// GET /events/:id/related — every object that uses one event, and how it uses
// it, behind the event dialog's Related Objects panel. Backend:
// modules/generic_functions/events/getEventRelatedObjects.js.
//
// This is not the same question as /events/:id/sources, in both directions. It
// includes the objects that WAIT on the event, which sources does not — those
// are what breaks when an event is renamed or deleted. And it excludes
// file_arrival, because a file watcher is the event's own configuration rather
// than another object using it; it belongs on the event's own form, where it
// already is.

export const RELATED_KINDS = ['node_wait', 'flow_node_wait', 'node_completion', 'flow_node_completion', 'runtime_limit', 'status_change_trigger'] as const;
export type RelatedObjectKind = (typeof RELATED_KINDS)[number];

/** The event the objects belong to. Projected server-side: the full row carries the file watcher's config. */
export interface RelatedEvent {
  readonly event_id: number;
  readonly name: string;
  readonly type: string;
  readonly zone_name: string;
}

/**
 * One related object: its identity, and the kind of relationship. Nothing else.
 *
 * The read carries no configuration — no auto-submit/auto-clear, no status, no
 * precondition, no scheduling window, no runtime-limit thresholds, no trigger
 * scope — and that is a deliberate limit rather than a gap to fill by widening
 * this type. Those values cannot be answered correctly at definition level: the
 * engine resolves a flow placement against the child definition's own settings
 * before falling back to a hard default, and that resolution does not exist
 * until the flow is submitted. Stating them anyway produces a panel that is
 * confidently wrong rather than silent.
 *
 * Not a discriminated union, for the same reason getEventSources is not: the
 * panel narrows by `kind` and renders a per-kind row by hand, and `union()`
 * would fail the whole response on an unrecognised discriminator — the wrong
 * trade for an informational panel where one unknown row should not cost the
 * operator every other one.
 */
export interface RelatedObject {
  readonly kind: RelatedObjectKind;
  // every kind except status_change_trigger
  readonly node_definition_id: number | null;
  readonly node_name: string | null;
  /** For the flow_node_* kinds this is the *flow's* zone, which can differ from the event's. */
  readonly zone_id: number | null;
  readonly zone_name: string | null;
  // flow_node_wait + flow_node_completion — the placement the wait or raise is configured
  // on. `node_definition_id` above names the child placed there; these name the flow and
  // the step, which is where the configuration lives and where it has to be changed.
  readonly flow_definition_id: number | null;
  readonly flow_definition_name: string | null;
  readonly flow_node_id: string | null;
  readonly flow_node_label: string | null;
  /**
   * The child's own zone, which is not `zone_name` above — that is the flow's. The node
   * library is not zone-scoped, so a flow can place a child from another zone.
   */
  readonly child_zone_name: string | null;
  // status_change_trigger
  readonly trigger_id: number | null;
  readonly trigger_name: string | null;
}

export interface EventRelatedObjectsResponse {
  readonly event: RelatedEvent;
  readonly related_objects: readonly RelatedObject[];
}

const eventSchema = object<RelatedEvent>({
  event_id: numeric(REJECT),
  name: str(),
  type: str(),
  zone_name: str(),
});

// Every kind-inapplicable field arrives explicitly null, but `nullable` is what
// the panel needs regardless: it reads each field only for the kinds that have it.
const relatedObjectSchema = object<RelatedObject>({
  kind: oneOf(RELATED_KINDS, REJECT),
  node_definition_id: nullable(numeric()),
  node_name: nullable(str()),
  zone_id: nullable(numeric()),
  zone_name: nullable(str()),
  flow_definition_id: nullable(numeric()),
  flow_definition_name: nullable(str()),
  flow_node_id: nullable(str()),
  flow_node_label: nullable(str()),
  child_zone_name: nullable(str()),
  trigger_id: nullable(numeric()),
  trigger_name: nullable(str()),
});

const responseSchema = object<EventRelatedObjectsResponse>({
  event: eventSchema,
  related_objects: array(relatedObjectSchema),
});

/**
 * GET `/events/:id/related`. Throws on transport error or structural validation
 * failure.
 *
 * `kind` is enforced per row: it selects which fields the panel reads and which
 * relationship it states, so a drifted one would render an object under the
 * wrong description with every field blank. Dropping that row keeps the rest
 * honest.
 */
export const getEventRelatedObjects = async (eventId: number): Promise<Received<EventRelatedObjectsResponse>> => {
  const res = await api.get(`/events/${encodeURIComponent(eventId)}/related`);
  return readResponse(res.data, { schema: responseSchema, source: 'getEventRelatedObjects' });
};
